import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { TrashIcon } from '@heroicons/react/24/solid';
import IsAirUrlService from './IsAirUrlService';

const PER_PAGE = 50;

const IsAirUrlSetting = () => {
  const [urlInput, setUrlInput] = useState('');
  const [list, setList] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PER_PAGE, total: 0, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const fetchList = async (page = 1) => {
    setIsLoading(true);
    try {
      const res = await IsAirUrlService.getList(page, PER_PAGE);
      setList(res.data || []);
      setPagination(res.pagination || { page: 1, limit: PER_PAGE, total: 0, totalPages: 0 });
    } catch (err) {
      toast.error(err.response?.data?.message || 'โหลดรายการไม่สำเร็จ');
      setList([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchList(1);
  }, []);

  const handleAdd = async (e) => {
    e?.preventDefault();
    // Support multiple URLs: one per line
    const lines = (urlInput || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (lines.length === 0) {
      toast.warning('กรุณากรอก URL (หนึ่งบรรทัดต่อหนึ่ง URL)');
      return;
    }
    setIsAdding(true);
    try {
      let added = 0;
      let skipped = 0; // already exists
      let failed = 0;
      for (const url of lines) {
        try {
          const res = await IsAirUrlService.addUrl(url);
          if (res.message === 'URL already exists' || res.message?.includes('already exists')) {
            skipped += 1;
          } else {
            added += 1;
          }
        } catch (err) {
          if (err.response?.status === 200 || err.response?.data?.message?.includes('already exists')) {
            skipped += 1;
          } else {
            failed += 1;
          }
        }
      }
      setUrlInput('');
      fetchList(1);
      if (added > 0) toast.success(`เพิ่ม ${added} URL แอร์เรียบร้อย${skipped > 0 ? ` (${skipped} ซ้ำข้าม)` : ''}${failed > 0 ? ` — ${failed} ไม่สำเร็จ` : ''}`);
      else if (skipped > 0) toast.info(`${skipped} URL มีในรายการแล้ว`);
      else if (failed > 0) toast.error(`เพิ่มไม่สำเร็จ ${failed} รายการ`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'เพิ่มไม่สำเร็จ');
    } finally {
      setIsAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('ลบ URL นี้จากรายการแอร์?')) return;
    try {
      await IsAirUrlService.deleteUrl(id);
      toast.success('ลบแล้ว');
      fetchList(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.message || 'ลบไม่สำเร็จ');
    }
  };

  const onPageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) fetchList(page);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">URL แอร์ (isAirUrl)</h2>
      <p className="text-sm text-gray-600 mb-4">
        เมื่อแอดมินใส่ URL ในฟอร์มสั่งซื้อ ถ้า URL ตรงกับรายการด้านล่าง ระบบจะแจ้งว่าเป็นแอร์และเลือกหิ้วแอร์ให้อัตโนมัติ
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: list with pagination (2/3) */}
        <div className="lg:col-span-2 overflow-hidden flex flex-col">
          <div className="overflow-y-auto shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg flex-1" style={{ maxHeight: 'calc(100vh - 220px)' }}>
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">กำลังโหลด...</div>
            ) : list.length === 0 ? (
              <div className="p-8 text-center text-gray-500">ยังไม่มี URL ในรายการ</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th scope="col" className="py-3 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">No</th>
                    <th scope="col" className="px-3 py-3 text-left text-sm font-semibold text-gray-900">URL</th>
                    <th scope="col" className="px-3 py-3 text-left text-sm font-semibold text-gray-900">วันที่เพิ่ม</th>
                    <th scope="col" className="relative py-3 pl-3 pr-4 sm:pr-6"><span className="sr-only">ลบ</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {list.map((row, index) => (
                    <tr key={row.id}>
                      <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm text-gray-900 sm:pl-6">
                        {(pagination.page - 1) * PER_PAGE + index + 1}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600 max-w-md truncate" title={row.url}>{row.url}</td>
                      <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                        {row.createdAt ? new Date(row.createdAt).toLocaleDateString('th-TH') : '-'}
                      </td>
                      <td className="relative whitespace-nowrap py-3 pl-3 pr-4 text-right sm:pr-6">
                        <button
                          type="button"
                          onClick={() => handleDelete(row.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-2 rounded-b-lg">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => onPageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  ก่อนหน้า
                </button>
                <button
                  onClick={() => onPageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  ถัดไป
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    แสดง <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> ถึง{' '}
                    <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> จาก{' '}
                    <span className="font-medium">{pagination.total}</span> รายการ
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => onPageChange(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                      className="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      ก่อนหน้า
                    </button>
                    <span className="relative inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700">
                      หน้า {pagination.page} / {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => onPageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.totalPages}
                      className="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      ถัดไป
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right: input URL (1/3) */}
        <div className="lg:col-span-1">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">เพิ่ม URL แอร์</h3>
            <form onSubmit={handleAdd}>
              <label htmlFor="isair-url" className="block text-xs font-medium text-gray-700 mb-1">URL (หนึ่งบรรทัดต่อหนึ่งลิงก์)</label>
              <textarea
                id="isair-url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder={'https://auctions.yahoo.co.jp/jp/auction/q1217119976\nhttps://auctions.yahoo.co.jp/jp/auction/p1216557596'}
                rows={6}
                className="mb-3 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 font-mono"
              />
              <button
                type="submit"
                disabled={isAdding}
                className="w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
              >
                {isAdding ? 'กำลังเพิ่ม...' : `เพิ่ม URL${urlInput.trim() ? ` (${urlInput.split(/\r?\n/).filter((l) => l.trim()).length} รายการ)` : ''}`}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IsAirUrlSetting;
